import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import axios from 'src/utils/axios';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Modern stil tanımları
const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      maxHeight: '85vh',
    },
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    color: '#ffffff',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '12px',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  dialogContent: {
    padding: '24px',
    backgroundColor: '#f8fafc',
  },
  tableContainer: {
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  tableHead: {
    backgroundColor: '#f1f5f9',
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#475569',
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      padding: '16px',
      borderBottom: '2px solid #e2e8f0',
    },
  },
  tableRow: {
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#fafafa',
    },
  },
  tableCell: {
    padding: '14px 16px',
    fontSize: '0.875rem',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
  },
  oldValueChip: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    fontWeight: 500,
    fontSize: '0.75rem',
    borderRadius: '6px',
    border: '1px solid #fecaca',
  },
  newValueChip: {
    backgroundColor: '#f0fdf4',
    color: '#16a34a',
    fontWeight: 500,
    fontSize: '0.75rem',
    borderRadius: '6px',
    border: '1px solid #bbf7d0',
  },
  fieldChip: {
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    fontWeight: 500,
    fontSize: '0.75rem',
    borderRadius: '6px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    gap: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '64px',
    color: '#cbd5e1',
    marginBottom: '16px',
  },
  dialogActions: {
    padding: '16px 24px',
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e2e8f0',
  },
  closeActionButton: {
    borderRadius: '12px',
    padding: '10px 24px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  projectChip: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#ffffff',
    fontWeight: 500,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  dateInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#64748b',
    fontSize: '0.8rem',
  },
};

const HavcLogs = ({ open, setOpen, projectId, HavcAddressData }) => {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const mainArray = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('HavcComboData')?.replace(/\\"/g, '"')) || [{}];
    } catch (e) {
      console.error("mainArray parse hatası:", e);
      return [{}];
    }
  }, []);

  const comboMap = {
    "Marka": "MARKA",
    "Bayi": "BAYI",
    "Bölge": "BOLGE",
    "Dosya Tipi": "DOSYA_TIPLERI",
    "Sistemin Durumu": "DURUM",
    "Proje Destek Servisi": "MENTOR_SERVIS",
    "Merkezi Kumanda Modeli": "MERKEZI_KLIMALAR",
    "Servis": "SERVIS",
    "Servis Yönetimi": "SERVIS_YONETMENI",
    "Sorumlu Teknisyen": "SORUMLU_TEKNISYEN"
  };

  const addressMap = {
    "İl": "IL",
    "İlçe": "ILCE",
    "Mahalle": "MAHALLE"
  };

  const getDescriptionFromId = (fieldName, id) => {
    const comboKey = comboMap[fieldName];
    const addressKey = addressMap[fieldName];

    if (comboKey && mainArray[0]?.[comboKey]) {
      const found = mainArray[0][comboKey].find(item => String(item.Id) === String(id));
      return found?.Dsc || id;
    }

    if (addressKey && HavcAddressData?.[0]?.[addressKey]) {
      const found = HavcAddressData[0][addressKey].find(item => String(item.Id) === String(id));
      return found?.Dsc || id;
    }

    return id;
  };

  const fetchData = () => {
    setLoading(true);
    axios
      .get('/GetHavcProjectLog', { params: { projectId } })
      .then(response => {
        setData(response.data || []);
      })
      .catch(error => {
        console.error('Veri alınırken hata:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, projectId]);

  const handleClose = () => {
    setOpen(false);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <Dialog 
      onClose={handleClose} 
      open={open} 
      fullWidth 
      maxWidth="lg"
      sx={styles.dialog}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.titleContent}>
          <Box sx={styles.titleIcon}>
            <HistoryIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '2px' }}>
              Proje Değişiklikleri
            </Typography>
            <Chip 
              label={`Proje #${projectId}`} 
              size="small" 
              sx={styles.projectChip}
            />
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        {loading ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={40} thickness={4} sx={{ color: '#8b5cf6' }} />
            <Typography sx={{ color: '#64748b' }}>
              Değişiklik geçmişi yükleniyor...
            </Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box sx={styles.emptyState}>
            <InfoOutlinedIcon sx={styles.emptyIcon} />
            <Typography variant="h6" sx={{ color: '#64748b', marginBottom: '8px' }}>
              Değişiklik Kaydı Yok
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
              Bu proje için henüz değişiklik kaydı bulunmuyor
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={styles.tableContainer} elevation={0}>
            <Table>
              <TableHead sx={styles.tableHead}>
                <TableRow>
                  <TableCell>Alan Adı</TableCell>
                  <TableCell>İlgilisi</TableCell>
                  <TableCell>Eski Değer</TableCell>
                  <TableCell>Yeni Değer</TableCell>
                  <TableCell>Yapan Kullanıcı</TableCell>
                  <TableCell>Tarihi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => {
                  const needsMapping = comboMap.hasOwnProperty(row.KUL_ALAN_ISMI) ||
                    addressMap.hasOwnProperty(row.KUL_ALAN_ISMI);

                  const eskiDeger = needsMapping
                    ? getDescriptionFromId(row.KUL_ALAN_ISMI, row.ESKI_DEGER)
                    : row.ESKI_DEGER;

                  const yeniDeger = needsMapping
                    ? getDescriptionFromId(row.KUL_ALAN_ISMI, row.YENI_DEGER)
                    : row.YENI_DEGER;

                  const ilgilisi = row.FIS_NO || row.SYSTEM_NAME || "-";

                  return (
                    <TableRow key={index} sx={styles.tableRow}>
                      <TableCell sx={styles.tableCell}>
                        <Chip 
                          label={row.KUL_ALAN_ISMI} 
                          size="small" 
                          sx={styles.fieldChip}
                        />
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <Typography sx={{ fontWeight: 500, color: '#475569' }}>
                          {ilgilisi}
                        </Typography>
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {eskiDeger ? (
                          <Chip 
                            label={eskiDeger} 
                            size="small" 
                            sx={styles.oldValueChip}
                          />
                        ) : (
                          <Typography sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.8rem' }}>
                            Boş
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {yeniDeger ? (
                          <Chip 
                            label={yeniDeger} 
                            size="small" 
                            sx={styles.newValueChip}
                          />
                        ) : (
                          <Typography sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.8rem' }}>
                            Boş
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <Box sx={styles.userInfo}>
                          <PersonIcon sx={{ fontSize: '16px', color: '#64748b' }} />
                          <Typography sx={{ fontWeight: 500 }}>
                            {row.KAYDEDEN_KISI}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <Box sx={styles.dateInfo}>
                          <CalendarTodayIcon sx={{ fontSize: '14px' }} />
                          {formatDate(row.KAYIT_TARIHI)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button 
          onClick={handleClose}
          variant="contained"
          color="secondary"
          endIcon={<CloseIcon />}
          sx={styles.closeActionButton}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HavcLogs;
